import React, { useState } from "react";
import { Save, ArrowLeft, Loader2 } from "lucide-react";
import { Layout } from "../../layouts/DashboardLayout";
import { countriesService } from "../../services/countriesService";

interface CountryForm {
  code: string;
  name: string;
  phonecode: string;
  devise: string;
}

const AddCountryPage: React.FC = () => {
  const [form, setForm] = useState<CountryForm>({
    code: "",
    name: "",
    phonecode: "",
    devise: "",
  });

  const [errors, setErrors] = useState<Partial<CountryForm>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Validation du formulaire
  const validateForm = (): boolean => {
    const newErrors: Partial<CountryForm> = {};

    if (!form.name.trim()) {
      newErrors.name = "Le nom du pays est requis";
    }

    if (!form.code.trim()) {
      newErrors.code = "Le code du pays est requis";
    } else if (form.code.length !== 2) {
      newErrors.code = "Le code doit contenir exactement 2 caractères";
    }

    if (!form.phonecode.trim()) {
      newErrors.phonecode = "L'indicatif téléphonique est requis";
    } else if (!/^\d+$/.test(form.phonecode)) {
      newErrors.phonecode = "L'indicatif doit contenir uniquement des chiffres";
    }

    if (!form.devise.trim()) {
      newErrors.devise = "La devise est requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Gestionnaire de changement des champs
  const handleChange = (field: keyof CountryForm, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "code" ? value.toUpperCase() : value,
    }));

    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Gestionnaire de soumission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);
      try {
        console.log("Ajouter pays:", form);
        // TODO: Appel API pour ajouter le pays

        // Simuler un délai
        await countriesService.createCountry(form);

        // Reset du formulaire après succès
        setForm({
          code: "",
          name: "",
          phonecode: "",
          devise: "",
        });

        alert("Pays ajouté avec succès !");
      } catch (error) {
        console.error("Erreur lors de l'ajout:", error);
        alert("Erreur lors de l'ajout du pays");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBack = () => {
    // TODO: Navigation vers la liste des pays
    window.history.back();
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour</span>
          </button>
        </div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Ajouter un Pays</h1>
        <p className="text-gray-600">Ajouter un nouveau pays à la plateforme</p>
      </div>

      <div className="flex justify-center">
        <div className="w-full max-w-2xl">
          <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nom du pays */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-3">
                  Nom du Pays *
                </label>
                <input
                  id="name"
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className={`w-full px-4 py-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 ${
                    errors.name ? "border-red-300 bg-red-50" : "border-gray-300"
                  }`}
                  placeholder="Ex: France"
                  disabled={isLoading}
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Code du pays */}
              <div>
                <label htmlFor="code" className="block text-sm font-semibold text-gray-700 mb-3">
                  Code Pays (ISO 2 lettres) *
                </label>
                <input
                  id="code"
                  type="text"
                  value={form.code}
                  onChange={(e) => handleChange("code", e.target.value)}
                  className={`w-full px-4 py-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 uppercase ${
                    errors.code ? "border-red-300 bg-red-50" : "border-gray-300"
                  }`}
                  placeholder="Ex: FR"
                  maxLength={2}
                  disabled={isLoading}
                />
                {errors.code && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.code}
                  </p>
                )}
              </div>

              {/* Indicatif téléphonique */}
              <div>
                <label htmlFor="phonecode" className="block text-sm font-semibold text-gray-700 mb-3">
                  Indicatif Téléphonique *
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                    +
                  </span>
                  <input
                    id="phonecode"
                    type="text"
                    value={form.phonecode}
                    onChange={(e) => handleChange("phonecode", e.target.value)}
                    className={`w-full pl-10 pr-4 py-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 ${
                      errors.phonecode ? "border-red-300 bg-red-50" : "border-gray-300"
                    }`}
                    placeholder="Ex: 33"
                    disabled={isLoading}
                  />
                </div>
                {errors.phonecode && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.phonecode}
                  </p>
                )}
              </div>

              {/* Devise */}
              <div>
                <label htmlFor="devise" className="block text-sm font-semibold text-gray-700 mb-3">
                  Devise *
                </label>
                <input
                  id="devise"
                  type="text"
                  value={form.devise}
                  onChange={(e) => handleChange("devise", e.target.value)}
                  className={`w-full px-4 py-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 ${
                    errors.devise ? "border-red-300 bg-red-50" : "border-gray-300"
                  }`}
                  placeholder="Ex: EUR"
                  disabled={isLoading}
                />
                {errors.devise && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {errors.devise}
                  </p>
                )}
              </div>

              {/* Boutons */}
              <div className="flex space-x-4 pt-8 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
                  disabled={isLoading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 font-medium shadow-md hover:shadow-lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Enregistrement...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      <span>Enregistrer le Pays</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddCountryPage;
